// components/whatsapp/SmartWhatsAppCTA.tsx
'use client';

/**
 * CTA WhatsApp global context-aware (header/footer) — 2026-06-11.
 *
 * Reemplaza el botón que abría SIEMPRE el intake de apartamentos. Al click
 * (no en render — así no necesita useSearchParams ni Suspense) lee
 * window.location + el beacon de página y resuelve con resolveWspCtaBehavior:
 *
 *  - bypass: hay búsqueda activa en la URL → directo a wa.me con el mensaje
 *    calificado (equivalente a "Enviar búsqueda por WhatsApp" de /reservas).
 *  - intake: abre el asistente con la variante y el contexto correctos
 *    (stay con niños, o tour con fecha única en páginas de tours).
 *
 * El cta_click_whatsapp del intake lo emite el sheet al salir; el del bypass
 * se emite aquí porque no hay sheet de por medio.
 */

import { useCallback, useState } from 'react';
import WhatsAppIntakeSheet, {
  type WhatsAppIntakeContext,
} from './WhatsAppIntakeSheet';
import {
  resolveWspCtaBehavior,
  readWspBeacon,
} from '@/lib/whatsapp/cta-context';
import { openTrackedWhatsApp } from '@/lib/whatsapp/open-tracked-whatsapp';
import {
  fireWspReservasConversion,
  fireWspToursConversion,
} from '@/lib/analytics/gtag-conversion';

interface Props {
  placement: 'header' | 'footer';
  className?: string;
  ariaLabel?: string;
  children: React.ReactNode;
}

export default function SmartWhatsAppCTA({ placement, className, ariaLabel, children }: Props) {
  const [open, setOpen] = useState(false);
  // Contexto por defecto (SSR / antes del primer click). El definitivo se
  // resuelve en cada click con la URL del momento.
  const [intakeContext, setIntakeContext] = useState<WhatsAppIntakeContext>({
    ctaPosition: `intake_${placement}`,
    entryContext: placement,
    source: placement,
  });

  const handleClick = useCallback(() => {
    const behavior = resolveWspCtaBehavior({
      pathname: window.location.pathname,
      search: window.location.search,
      beacon: readWspBeacon(document.body.dataset),
      placement,
    });

    if (behavior.kind === 'bypass') {
      openTrackedWhatsApp(behavior.message, {
        source: behavior.source,
        cta_position: behavior.ctaPosition,
        entry_context: behavior.entryContext,
        arrival: behavior.arrival,
        departure: behavior.departure,
        num_adults: behavior.numAdults,
        guests_total: behavior.guestsTotal ?? behavior.numAdults,
        apartment_id: behavior.apartmentId,
        tour_slug: behavior.tourSlug,
      });
      if (behavior.vertical === 'tour') {
        fireWspToursConversion({
          cta_position: behavior.ctaPosition,
          tour_slug: behavior.tourSlug,
          tour_date: behavior.arrival,
          guests: behavior.guestsTotal ?? behavior.numAdults,
        });
      } else {
        fireWspReservasConversion({
          cta_position: behavior.ctaPosition,
          arrival: behavior.arrival,
          departure: behavior.departure,
          num_adults: behavior.numAdults,
          apartment_id: behavior.apartmentId,
        });
      }
      return;
    }

    setIntakeContext(behavior.context);
    setOpen(true);
  }, [placement]);

  return (
    <>
      <button type="button" onClick={handleClick} aria-label={ariaLabel} className={className}>
        {children}
      </button>
      <WhatsAppIntakeSheet open={open} onClose={() => setOpen(false)} context={intakeContext} />
    </>
  );
}
